import { useEffect, useState, type FormEvent } from "react";
import { saveMailAccount, testMailConnection } from "@/lib/api/accounts";
import { MAIL_GOOGLE_OAUTH_ENABLED } from "@/lib/constants/googleOAuth";
import { MAIL_MICROSOFT_OAUTH_ENABLED } from "@/lib/constants/microsoftOAuth";
import { connectGmailWithGoogle } from "@/lib/accounts/gmailOAuthPopup";
import { connectMicrosoftMailbox } from "@/lib/accounts/microsoftOAuthPopup";
import { OAuthHintBanner } from "./OAuthHintBanner";
import { ManualServerFields } from "./ManualServerFields";
import { GoogleBrandIcon } from "./icons/GoogleBrandIcon";
import { MicrosoftBrandIcon } from "./icons/MicrosoftBrandIcon";
import type { MailAccountSummary, MailProviderPreset, MailServerPreset } from "@/lib/types/mail";

type Props = {
  provider: MailProviderPreset | null;
  onSuccess: (account?: MailAccountSummary) => void;
  /** Bouton Google affiché dans AccountConnectPicker */
  hideGoogleOAuth?: boolean;
  /** Bouton Microsoft affiché dans AccountConnectPicker */
  hideMicrosoftOAuth?: boolean;
};

function isManualValid(imap: MailServerPreset, smtp: MailServerPreset): boolean {
  return Boolean(imap.host.trim() && smtp.host.trim() && imap.port > 0 && smtp.port > 0);
}

export function AccountConnectForm({
  provider,
  onSuccess,
  hideGoogleOAuth = false,
  hideMicrosoftOAuth = false,
}: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [imap, setImap] = useState<MailServerPreset>({
    host: "",
    port: 993,
    encryption: "ssl",
  });
  const [smtp, setSmtp] = useState<MailServerPreset>({
    host: "",
    port: 587,
    encryption: "starttls",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isManual = provider?.id === "manual";
  const isGmailOAuth =
    MAIL_GOOGLE_OAUTH_ENABLED &&
    !hideGoogleOAuth &&
    provider?.id === "gmail" &&
    provider.oauthAvailable === true;
  const isMicrosoftOAuth =
    MAIL_MICROSOFT_OAUTH_ENABLED &&
    !hideMicrosoftOAuth &&
    provider?.id === "microsoft" &&
    provider.oauthAvailable === true;
  const oauthOnly =
    (provider?.id === "gmail" &&
      MAIL_GOOGLE_OAUTH_ENABLED &&
      provider.oauthAvailable === true) ||
    (provider?.id === "microsoft" &&
      MAIL_MICROSOFT_OAUTH_ENABLED &&
      provider.oauthAvailable === true);

  useEffect(() => {
    if (!provider) return;
    if (provider.id === "manual") {
      setImap(provider.imap);
      setSmtp(provider.smtp);
    }
  }, [provider]);

  const credentialsOk = Boolean(provider && email.trim() && password);
  const manualOk = !isManual || isManualValid(imap, smtp);
  const disabled = !credentialsOk || !manualOk || busy;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!provider) return;

    setBusy(true);
    setError(null);

    const payload = {
      providerId: provider.id,
      email: email.trim(),
      password,
      ...(isManual ? { imap, smtp } : {}),
    };

    const test = await testMailConnection(payload);
    if (!test.ok) {
      setError(test.message);
      setBusy(false);
      return;
    }

    const save = await saveMailAccount(payload);
    if (!save.ok) {
      setError(save.message);
      setBusy(false);
      return;
    }

    setPassword("");
    setBusy(false);
    onSuccess(save.data.account);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <OAuthHintBanner provider={provider} />

      {!provider ? (
        <p className="text-sm text-[var(--mail-muted)]">
          Choisissez d&apos;abord votre fournisseur mail.
        </p>
      ) : (
        <>
          {isGmailOAuth ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => void connectGmailWithGoogle().then(onSuccess).catch((err) => {
                  setError(err instanceof Error ? err.message : "Connexion Google impossible.");
                })}
                disabled={busy}
                className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl border border-[var(--mail-border-soft)] bg-white px-4 py-3 text-sm font-semibold text-[var(--mail-text)] shadow-sm transition hover:bg-[var(--mail-surface-hover)] disabled:opacity-50"
              >
                <GoogleBrandIcon className="h-5 w-5" />
                Continuer avec Google
              </button>
            </div>
          ) : null}

          {isMicrosoftOAuth ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => void connectMicrosoftMailbox().then(onSuccess).catch((err) => {
                  setError(err instanceof Error ? err.message : "Connexion Microsoft impossible.");
                })}
                disabled={busy}
                className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl border border-[var(--mail-border-soft)] bg-white px-4 py-3 text-sm font-semibold text-[var(--mail-text)] shadow-sm transition hover:bg-[var(--mail-surface-hover)] disabled:opacity-50"
              >
                <MicrosoftBrandIcon className="h-5 w-5" />
                Continuer avec Microsoft
              </button>
            </div>
          ) : null}

          {oauthOnly ? null : isManual ? (
            <ManualServerFields
              imap={imap}
              smtp={smtp}
              onImapChange={setImap}
              onSmtpChange={setSmtp}
            />
          ) : (
            <div className="mail-form-info-box px-3 py-2.5">
              <div>
                <strong>IMAP</strong> {provider.imap.host}:{provider.imap.port} (
                {provider.imap.encryption.toUpperCase()})
              </div>
              <div className="mt-0.5">
                <strong>SMTP</strong> {provider.smtp.host}:{provider.smtp.port} (
                {provider.smtp.encryption.toUpperCase()})
              </div>
            </div>
          )}

          {oauthOnly ? null : (
          <>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-[var(--mail-text)]">Adresse e-mail</span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mail-form-input"
              placeholder={
                isManual
                  ? "vous@domaine.fr"
                  : isGmailOAuth
                    ? "vous@gmail.com"
                    : isMicrosoftOAuth
                      ? "vous@outlook.com"
                      : "vous@orange.fr"
              }
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-[var(--mail-text)]">Mot de passe</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mail-form-input"
              placeholder={
                provider.modernAuth
                  ? "Mot de passe d'application"
                  : "Mot de passe du compte mail"
              }
            />
          </label>
          </>
          )}
        </>
      )}

      {error ? (
        <p className="text-sm text-[var(--mail-danger-text)]" role="alert">
          {error}
        </p>
      ) : null}

      {provider && !oauthOnly ? (
        <button
          type="submit"
          disabled={disabled}
          className="w-full rounded-xl bg-[var(--mail-accent)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 disabled:opacity-50"
        >
          {busy ? "Connexion en cours…" : "Connecter ma boîte mail"}
        </button>
      ) : null}
    </form>
  );
}
